/**
 * API Client Configuration
 *
 * Configurable to use either real API or mock data via environment variables.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
}

// Token getter function - will be set by AuthContext (shared with services/api.ts)
let getAccessTokenFn: (() => Promise<string | null>) | null = null;

export const setTokenGetterForClient = (fn: () => Promise<string | null>) => {
  getAccessTokenFn = fn;
};

/**
 * Get authentication headers with MSAL token
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Get token from MSAL if available
  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Token acquisition failed - request will go without token
    }
  }

  return headers;
}

/**
 * Base fetch wrapper with error handling and retry logic
 */
export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Get auth headers (async because of token acquisition)
  const authHeaders = await getAuthHeaders();

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      // Try to parse error response
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If error response is not JSON, use default message
      }

      const error: ApiError = {
        message: errorMessage,
        code: response.status.toString(),
        status: response.status,
      };
      throw error;
    }

    // Handle empty responses (e.g., 204 No Content)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return {} as T;
    }

    return response.json();
  } catch (error) {
    // Network errors or other exceptions
    if (error instanceof TypeError) {
      throw {
        message: 'Network error: Unable to reach the server',
        code: 'NETWORK_ERROR',
        details: error,
      } as ApiError;
    }

    // Re-throw ApiError as-is
    if ((error as ApiError).message) {
      throw error;
    }

    // Unknown error
    throw {
      message: 'An unexpected error occurred',
      details: error,
    } as ApiError;
  }
}

/**
 * POST request helper
 */
export async function apiPost<T>(
  endpoint: string,
  data: unknown,
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * GET request helper
 */
export async function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'GET',
  });
}

/**
 * PUT request helper
 */
export async function apiPut<T>(
  endpoint: string,
  data: unknown,
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * PATCH request helper
 */
export async function apiPatch<T>(
  endpoint: string,
  data?: unknown,
): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE request helper
 */
export async function apiDelete<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'DELETE',
  });
}

/**
 * Get API base URL
 */
export const getApiBaseUrl = () => API_BASE_URL;

/**
 * Upload file via POST request with FormData
 */
export async function apiFormDataPost<T>(
  endpoint: string,
  formData: FormData,
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Get auth headers WITHOUT Content-Type (browser sets it automatically for FormData with boundary)
  const headers: Record<string, string> = {};

  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Token acquisition failed - request will go without token
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // If error response is not JSON, use default message
    }

    const error: ApiError = {
      message: errorMessage,
      code: response.status.toString(),
      status: response.status,
    };
    throw error;
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return {} as T;
  }

  return response.json();
}

/**
 * Result of a FormData POST that may legitimately return a non-2xx status with a JSON body
 * (e.g. bulk upload returning 400 with a per-line breakdown). Unlike {@link apiFormDataPost},
 * this does NOT throw on 4xx/5xx — the caller inspects {@link FormDataPostResult.status}.
 * Only genuine network failures reject.
 */
export interface FormDataPostResult<T> {
  status: number;
  data: T | null;
}

export async function apiFormDataPostAllowError<T>(
  endpoint: string,
  formData: FormData,
): Promise<FormDataPostResult<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  // No Content-Type: the browser sets multipart boundary automatically for FormData.
  const headers: Record<string, string> = {};

  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Token acquisition failed - request will go without token
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  const contentType = response.headers.get('content-type');
  let data: T | null = null;
  if (contentType && contentType.includes('application/json')) {
    try {
      data = (await response.json()) as T;
    } catch {
      data = null;
    }
  }

  return { status: response.status, data };
}

/**
 * Download blob (file) via POST request
 */
export async function apiBlobPost(
  endpoint: string,
  data: unknown,
): Promise<BlobResponse> {
  const url = `${API_BASE_URL}${endpoint}`;
  const authHeaders = await getAuthHeaders();

  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers.get('content-disposition');
  let filename: string | undefined;

  if (contentDisposition) {
    // Try to extract filename from Content-Disposition header
    // Format: attachment; filename=somefile.xlsx or filename="somefile.xlsx"
    const matches = /filename[^;=\n]*=\s*["']?([^"';]+)["']?/i.exec(contentDisposition);
    if (matches && matches[1]) {
      filename = matches[1].trim();
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}

/**
 * Download blob (file) via POST request with FormData body.
 * Used when the request must include both file and JSON parts (multipart/form-data).
 */
export async function apiBlobFormDataPost(
  endpoint: string,
  formData: FormData,
): Promise<BlobResponse> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {};

  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Token acquisition failed - request will go without token
    }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  const contentDisposition = response.headers.get('content-disposition');
  let filename: string | undefined;

  if (contentDisposition) {
    const matches = /filename[^;=\n]*=\s*["']?([^"';]+)["']?/i.exec(contentDisposition);
    if (matches && matches[1]) {
      filename = matches[1].trim();
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}

/**
 * Response with blob and optional filename from Content-Disposition header
 */
export interface BlobResponse {
  blob: Blob;
  filename?: string;
}

/**
 * Download blob (file) via GET request
 */
export async function apiBlobGet(endpoint: string): Promise<BlobResponse> {
  const url = `${API_BASE_URL}${endpoint}`;

  // For GET requests, only include Authorization and Accept headers (no Content-Type)
  const headers: Record<string, string> = {
    'Accept': '*/*', // Accept any content type for file downloads
  };

  if (getAccessTokenFn) {
    try {
      const token = await getAccessTokenFn();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Token acquisition failed - request will go without token
    }
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    // Try to get error details from response body
    let errorDetails = '';
    try {
      const text = await response.text();
      errorDetails = text ? ` - ${text}` : '';
    } catch {
      // Ignore if we can't read the body
    }
    throw new Error(`API Error: ${response.status} ${response.statusText}${errorDetails}`);
  }

  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers.get('content-disposition');
  let filename: string | undefined;

  if (contentDisposition) {
    // Try to extract filename from Content-Disposition header
    // Format: attachment; filename=somefile.xlsx or filename="somefile.xlsx"
    const matches = /filename[^;=\n]*=\s*["']?([^"';]+)["']?/i.exec(contentDisposition);
    if (matches && matches[1]) {
      filename = matches[1].trim();
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}
